import React from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { SimplePageLayout } from "../../components/layout";
import { Check } from "lucide-react";

export const ParentAppPage = () => {
  const { language } = useLanguage();
  
  return (
    <SimplePageLayout>
      <div className="max-w-3xl mx-auto">
        <span className="calli-badge mb-4">📱 {language === 'fr' ? 'Application' : 'App'}</span>
        <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
          {language === 'fr' ? 'Application Parent Calli' : 'Calli Parent App'}
        </h1>
        
        <div className="prose prose-lg text-gray-600 space-y-6">
          <p className="text-xl leading-relaxed">
            {language === 'fr' 
              ? "L'application parent Calli vous permet de configurer et gérer le téléphone de votre enfant en toute simplicité."
              : "The Calli parent app lets you easily configure and manage your child's phone."}
          </p>
          
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 space-y-4">
            <h2 className="text-2xl font-bold text-gray-900">
              {language === 'fr' ? 'Fonctionnalités principales' : 'Key Features'}
            </h2>
            <ul className="space-y-3">
              {[
                language === 'fr' ? "Ajoutez et modifiez les 8 contacts autorisés" : "Add and edit the 8 authorized contacts",
                language === 'fr' ? "Associez une photo à chaque contact" : "Associate a photo with each contact",
                language === 'fr' ? "Localisez Calli à tout moment" : "Locate Calli anytime",
                language === 'fr' ? "Recevez des alertes batterie faible" : "Receive low battery alerts",
                language === 'fr' ? "Consultez l'historique des appels" : "View call history",
                language === 'fr' ? "Désactivez Calli à distance si besoin" : "Remotely disable Calli if needed",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="bg-orange-50 rounded-2xl p-8 border border-orange-100">
            <p className="text-orange-700 font-medium">
              {language === 'fr' 
                ? "📲 L'application sera disponible sur iOS et Android dès la livraison de votre Calli."
                : "📲 The app will be available on iOS and Android when your Calli is delivered."}
            </p>
          </div>
        </div>
      </div>
    </SimplePageLayout>
  );
};

export const AccessoriesPage = () => {
  const { language } = useLanguage();
  
  const accessories = [
    {
      name: language === 'fr' ? 'Coque de protection' : 'Protective case',
      desc: language === 'fr' ? 'Protection renforcée pour les aventuriers' : 'Enhanced protection for adventurers',
      price: '14,90€',
      soon: true
    },
    {
      name: language === 'fr' ? 'Cordon tour de cou' : 'Neck strap',
      desc: language === 'fr' ? 'Pour ne jamais perdre Calli' : 'Never lose Calli',
      price: '9,90€',
      soon: true
    },
    {
      name: language === 'fr' ? 'Chargeur supplémentaire' : 'Extra charger',
      desc: language === 'fr' ? 'Un chargeur pour chaque lieu' : 'A charger for every place',
      price: '12,90€',
      soon: true
    },
  ];
  
  return (
    <SimplePageLayout>
      <div className="max-w-3xl mx-auto">
        <span className="calli-badge mb-4">🎁 {language === 'fr' ? 'Accessoires' : 'Accessories'}</span>
        <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
          {language === 'fr' ? 'Accessoires Calli' : 'Calli Accessories'}
        </h1>
        
        <p className="text-xl text-gray-600 mb-8">
          {language === 'fr' 
            ? "Complétez l'expérience Calli avec nos accessoires conçus pour les enfants."
            : "Complete the Calli experience with our kid-friendly accessories."}
        </p>
        
        <div className="grid gap-4">
          {accessories.map((acc, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-900">{acc.name}</h3>
                <p className="text-gray-500">{acc.desc}</p>
              </div>
              <div className="text-right">
                <p className="font-bold text-orange-500">{acc.price}</p>
                {acc.soon && (
                  <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full">
                    {language === 'fr' ? 'Bientôt' : 'Soon'}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </SimplePageLayout>
  );
};

export const PrivacyPage = () => {
  const { language } = useLanguage();
  
  return (
    <SimplePageLayout>
      <div className="max-w-3xl mx-auto">
        <span className="calli-badge mb-4">🔒 {language === 'fr' ? 'Confidentialité' : 'Privacy'}</span>
        <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
          {language === 'fr' ? 'Politique de confidentialité' : 'Privacy Policy'}
        </h1>
        
        <div className="prose prose-lg text-gray-600 space-y-6">
          <p>{language === 'fr' ? 'Dernière mise à jour : Décembre 2025' : 'Last updated: December 2025'}</p>
          
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Données collectées' : 'Data collected'}
            </h2>
            <p>
              {language === 'fr' 
                ? "Nous collectons uniquement les données nécessaires au fonctionnement du service : nom, email, informations de paiement pour les précommandes."
                : "We only collect data necessary for the service: name, email, payment information for preorders."}
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Protection des données' : 'Data protection'}
            </h2>
            <p>
              {language === 'fr' 
                ? "Vos données sont stockées de manière sécurisée et ne sont jamais partagées avec des tiers à des fins commerciales."
                : "Your data is stored securely and never shared with third parties for commercial purposes."}
            </p>
          </div>
        </div>
      </div>
    </SimplePageLayout>
  );
};

export const TermsPage = () => {
  const { language } = useLanguage();
  
  return (
    <SimplePageLayout>
      <div className="max-w-3xl mx-auto">
        <span className="calli-badge mb-4">📜 {language === 'fr' ? 'CGV' : 'Terms'}</span>
        <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
          {language === 'fr' ? 'Conditions Générales de Vente' : 'Terms and Conditions'}
        </h1>
        
        <div className="prose prose-lg text-gray-600 space-y-6">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Précommandes' : 'Preorders'}
            </h2>
            <p>
              {language === 'fr' 
                ? "L'acompte de 15€ est entièrement remboursable si l'objectif de financement n'est pas atteint. Le solde de 64€ sera prélevé avant expédition."
                : "The €15 deposit is fully refundable if the funding goal is not reached. The remaining €64 will be charged before shipping."}
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Livraison' : 'Delivery'}
            </h2>
            <p>
              {language === 'fr' 
                ? "Livraison prévue en Septembre 2026. Livraison gratuite en France métropolitaine."
                : "Expected delivery in September 2026. Free shipping in mainland France."}
            </p>
          </div>
        </div>
      </div>
    </SimplePageLayout>
  );
};

export const RefundPage = () => {
  const { language } = useLanguage();
  
  return (
    <SimplePageLayout>
      <div className="max-w-3xl mx-auto">
        <span className="calli-badge mb-4">💰 {language === 'fr' ? 'Remboursement' : 'Refund'}</span>
        <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
          {language === 'fr' ? 'Politique de remboursement' : 'Refund Policy'}
        </h1>
        
        <div className="prose prose-lg text-gray-600 space-y-6">
          <div className="bg-green-50 rounded-2xl p-8 border border-green-100">
            <h2 className="text-xl font-bold text-green-800 mb-4">
              {language === 'fr' ? '✓ Notre garantie' : '✓ Our guarantee'}
            </h2>
            <p className="text-green-700">
              {language === 'fr' 
                ? "Si l'objectif de financement n'est pas atteint, vous êtes remboursé à 100% sous 7 jours. Aucune question posée."
                : "If the funding goal is not reached, you get a 100% refund within 7 days. No questions asked."}
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Après réception' : 'After delivery'}
            </h2>
            <p>
              {language === 'fr' 
                ? "Vous disposez de 30 jours après réception pour retourner votre Calli et être remboursé intégralement si le produit ne vous convient pas."
                : "You have 30 days after delivery to return your Calli for a full refund if the product doesn't suit you."}
            </p>
          </div>
        </div>
      </div>
    </SimplePageLayout>
  );
};

export const CareersPage = () => {
  const { language } = useLanguage();
  
  return (
    <SimplePageLayout>
      <div className="max-w-3xl mx-auto">
        <span className="calli-badge mb-4">💼 {language === 'fr' ? 'Carrières' : 'Careers'}</span>
        <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
          {language === 'fr' ? "Rejoignez l'aventure Calli" : 'Join the Calli adventure'}
        </h1>
        
        <p className="text-xl text-gray-600 mb-8">
          {language === 'fr' 
            ? "Nous recherchons des talents passionnés pour révolutionner la communication des enfants."
            : "We're looking for passionate talents to revolutionize children's communication."}
        </p>
        
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center">
          <p className="text-gray-600 mb-4">
            {language === 'fr' 
              ? "Aucune offre disponible pour le moment, mais nous sommes toujours à l'écoute des candidatures spontanées !"
              : "No positions available at the moment, but we're always open to spontaneous applications!"}
          </p>
          <p className="text-orange-600 font-bold">jobs@calli.fr</p>
        </div>
      </div>
    </SimplePageLayout>
  );
};
